import { useMemo, useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { Card, CardTitle, CardDescription, Button, Skeleton } from '@/components/ui';
import { KeywordTable, type KeywordRow } from '@/components/keyword';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { formatNumber, type BulkResultItem } from '@keywordgod/shared';

export function BulkAnalysis() {
  const user = useAuthStore((s) => s.user);
  if (!user || user.status !== 'active') {
    return (
      <Card className="mx-auto max-w-2xl text-center">
        <CardTitle>이용 권한이 필요합니다</CardTitle>
        <CardDescription className="mt-2">
          승인된 챌린지 회원만 대량 분석을 사용할 수 있습니다.
        </CardDescription>
      </Card>
    );
  }
  return <BulkRunner />;
}

function BulkRunner() {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [items, setItems] = useState<BulkResultItem[]>([]);
  const [summary, setSummary] = useState<{ total: number; succeeded: number; failed: number } | null>(null);

  const keywords = useMemo(
    () =>
      Array.from(
        new Set(text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)),
      ),
    [text],
  );

  async function submit() {
    setBusy(true);
    setErr(null);
    setItems([]);
    setSummary(null);
    setProgress(5);

    // 의사 진행률 — 서버 응답까지 부드럽게 이동
    const tick = setInterval(() => setProgress((p) => Math.min(90, p + 4)), 250);
    try {
      const res = await api.keyword.bulk(keywords);
      setItems(res.items);
      setSummary({ total: res.total, succeeded: res.succeeded, failed: res.failed });
      setProgress(100);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      clearInterval(tick);
      setBusy(false);
    }
  }

  function onCsvUpload(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const csv = String(reader.result ?? '');
      // 첫 열의 모든 값을 키워드로 취급
      const lines = csv
        .split(/\r?\n/)
        .map((l) => l.split(/[,\t]/)[0]?.trim())
        .filter(Boolean) as string[];
      setText(lines.join('\n'));
    };
    reader.readAsText(file);
  }

  const rows: KeywordRow[] = items.map((it) => ({
    keyword: it.keyword,
    totalSearch: it.data?.metrics.totalSearch,
    blogTotal: it.data?.metrics.blogTotal,
    saturation: it.data?.metrics.saturation.value,
    honeyScore: it.data?.metrics.honeyScore.value,
    opportunity: it.data?.metrics.opportunity.value,
    godScore: it.data?.godScore ?? 0,
    grade: it.data?.grade ?? 'D',
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardTitle>대량 키워드 분석</CardTitle>
        <CardDescription>
          한 줄에 한 키워드씩 (최대 100개). CSV 파일 업로드도 지원합니다.
        </CardDescription>

        <textarea
          className="mt-4 h-64 w-full rounded-xl border border-gray-800 bg-gray-950 p-3 text-sm font-mono text-white placeholder:text-gray-500 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          placeholder={`예시:\n다이어트 보조제\n단백질 쉐이크\n맥북 프로\n...`}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-xs text-gray-500">{keywords.length} / 100 개</span>
          <label className="ml-auto inline-flex items-center gap-2 cursor-pointer text-sm text-gray-300 hover:text-gold">
            <FileSpreadsheet className="h-4 w-4" />
            CSV 업로드
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onCsvUpload(f);
                e.target.value = '';
              }}
            />
          </label>
          <Button
            onClick={submit}
            disabled={busy || keywords.length === 0 || keywords.length > 100}
          >
            {busy ? '분석 중…' : `${keywords.length}개 분석 시작`}
          </Button>
        </div>

        {busy && (
          <div className="mt-4">
            <div className="h-2 w-full rounded-full bg-gray-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FFA500] to-[#FFD700] transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-gray-400 tabular">{progress}% — 캐시된 키워드는 즉시 반환됩니다</p>
          </div>
        )}

        {err && <p className="mt-3 text-sm text-red-400">{err}</p>}
      </Card>

      {summary && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>
              결과 — 총 {formatNumber(summary.total)} · 성공 {formatNumber(summary.succeeded)} · 실패 {formatNumber(summary.failed)}
            </CardTitle>
            <Button variant="secondary" size="sm" onClick={() => downloadCsv(items)}>
              CSV 다운로드
            </Button>
          </div>
          <div className="mt-4">
            <KeywordTable
              rows={rows}
              columns={[
                { key: 'keyword', label: '키워드' },
                { key: 'totalSearch', label: '검색량', align: 'right' },
                { key: 'blogTotal', label: '문서수', align: 'right' },
                { key: 'saturation', label: '포화', align: 'right' },
                { key: 'honeyScore', label: '꿀통', align: 'right' },
                { key: 'opportunity', label: '기회', align: 'right' },
                { key: 'godScore', label: 'GodScore', align: 'right' },
              ]}
            />
          </div>
        </Card>
      )}

      {busy && items.length === 0 && (
        <Card>
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function downloadCsv(items: BulkResultItem[]) {
  const header = ['keyword', 'godScore', 'grade', 'totalSearch', 'blogTotal', 'saturation', 'honeyScore', 'opportunity', 'compIdx', 'status'];
  const rows = items.map((it) => {
    const d = it.data;
    return [
      escape(it.keyword),
      d?.godScore ?? '',
      d?.grade ?? '',
      d?.metrics.totalSearch ?? '',
      d?.metrics.blogTotal ?? '',
      d?.metrics.saturation.value ?? '',
      d?.metrics.honeyScore.value ?? '',
      d?.metrics.opportunity.value ?? '',
      d?.metrics.compIdx ?? '',
      it.status,
    ].join(',');
  });
  const csv = [header.join(','), ...rows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `keywordgod-bulk-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function escape(v: string) {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
